import { useState } from 'react';
import { Controller } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form/dist/types';
import { toast } from 'react-toastify';

import classNames from 'classnames';
import { useDebounceValue } from 'usehooks-ts';

import type { DeserializedUser, RequestSignResult, WalletType } from '@api';

import { PasskeysRound } from '@uikit/icons/PassKeys';
import { WalletConnect } from '@uikit/icons/WalletConnect';
import { Collapse, Spinner } from '@uikit/index';

import { namedWeb3Errors } from '@shared/constants';
import displayErrorToast, { normalizeError } from '@shared/helpers/displayErrorToast';
import displayWalletErrorToast from '@shared/helpers/displayWalletErrorToast';
import { suppressAccountChangeLogout } from '@shared/helpers/web3/hooks/useListenToAccountChange';
import type { RequestSignProps } from '@shared/helpers/web3/hooks/useWeb3';
import useWeb3 from '@shared/helpers/web3/hooks/useWeb3';

import { useAddWalletMutation, useCheckUserNameQuery } from '@query/user';

import { CopyButton } from '@ui/uikit/components/common/CopyButton';
import { DialogButtonsWrapper } from '@ui/uikit/components/common/Dialog';
import { FormInput } from '@ui/uikit/components/common/FormInput';
import { cn } from '@ui/uikit/utils';

import { ProfileEmailField } from './ProfileEmailField';
import { WalletConnectType } from './WalletsList/model/walletsConfig';
import { WalletsList } from './WalletsList/WalletsList';

type Props = {
  form: UseFormReturn<{
    username: string;
    email: string;
  }>;
  onSubmitHandler: () => void;
  hideInputFields?: boolean;
  disabled?: boolean;
  user: DeserializedUser;
  showEmailInputOnly?: boolean;
  isUpdatingUserName?: boolean;
};

export const CompleteYourMagicIdForm = ({
  form,
  onSubmitHandler,
  hideInputFields,
  user,
  showEmailInputOnly,
  isUpdatingUserName,
}: Props) => {
  const [walletConnectType, setWalletConnectType] = useState<WalletType | undefined>();

  const { requestSign, isLoading, setIsLoading } = useWeb3();
  const { mutateAsync: addWallet, isPending: isAdding } = useAddWalletMutation();
  const isDisabled = isLoading || isAdding;

  const { control, watch } = form;

  const resetState = () => {
    setIsLoading(false);
  };

  const handleSignResult = async (signResult?: RequestSignResult) => {
    if (!signResult) {
      return setIsLoading(false);
    }

    try {
      const { address, walletType, network } = signResult;

      await addWallet({ network, wallet: walletType, address });

      toast.success('Wallet successfully assigned');
      resetState();
    } catch (error) {
      setIsLoading(false);

      const normalizedError: Error = normalizeError(error);

      if (normalizedError.message === namedWeb3Errors.SIGNATURE_VERIFICATION_HAS_FAILED) {
        throw normalizedError;
      }

      displayErrorToast({ error });
    }
  };

  const connectWallet = async (connectType: WalletType) => {
    try {
      setIsLoading(true);
      setWalletConnectType(connectType);

      const signProps: RequestSignProps = {
        walletType: connectType,
        signType: 'SIWE',
        message: 'Confirm adding a wallet',
      };

      suppressAccountChangeLogout();

      const signResult = await requestSign(signProps);

      await handleSignResult(signResult);
    } catch (error) {
      resetState();
      displayWalletErrorToast({ error });
    }
  };

  const hasWallets = Boolean(user?.wallets.length);
  const evmWallets =
    user?.wallets.filter((wallet) => wallet.attributes.network !== WalletConnectType.TON) || [];

  const usernameFromMagicId = user?.attributes.name ?? '';
  const [username, setUsername] = useState<string>(usernameFromMagicId);
  const watchedUsername = watch('username');
  const [debouncedUsername] = useDebounceValue(watchedUsername, 600);
  const { isFetching: isCheckUserNameLoading, data } = useCheckUserNameQuery(
    debouncedUsername === usernameFromMagicId ? null : debouncedUsername,
  );
  const [usernameValidation] = useDebounceValue(
    !usernameFromMagicId && debouncedUsername === username && !isCheckUserNameLoading ? data : null,
    600,
  );
  const disabled = !!(usernameFromMagicId || isUpdatingUserName);

  return (
    <form className="flex flex-col w-full" onSubmit={onSubmitHandler}>
      {!hideInputFields && (
        <>
          {!showEmailInputOnly && (
            <>
              <Controller
                control={control}
                name="username"
                render={({ field: { ref, onChange, value, ...field }, fieldState }) => {
                  return (
                    <FormInput
                      inputRef={ref}
                      data-qa="hot-offer-username-input"
                      onValueChange={(e) => {
                        setUsername(e);
                        onChange(e);
                      }}
                      value={value || username}
                      label="Username"
                      className={classNames({
                        'mb-8': !(!usernameFromMagicId && usernameValidation),
                      })}
                      isError={!!fieldState.error}
                      hint={fieldState.error?.message}
                      disabled={Boolean(user?.attributes.name)}
                      afterInput={
                        <CopyButton
                          data-skeleton-target="base"
                          value={value}
                          className="msq-btn msq-btn-icon-lg msq-btn-secondary"
                        />
                      }
                      {...field}
                    />
                  );
                }}
              />
              <Collapse isOpen={!!(!usernameFromMagicId && usernameValidation)}>
                {!usernameFromMagicId && usernameValidation && (
                  <p
                    className={classNames('mb-8 form-control-helper-text', {
                      'form-control-helper-text-error': !usernameValidation.isValid,
                      'form-control-helper-text-success': usernameValidation.isValid,
                      'form-control-helper-text-disabled': disabled,
                    })}
                    data-qa="profile-popup-username-helper-text"
                  >
                    {usernameValidation.message}
                  </p>
                )}
              </Collapse>
            </>
          )}
          <ProfileEmailField
            className={cn({
              'mb-0': showEmailInputOnly,
              'mb-8': !showEmailInputOnly,
            })}
          />
        </>
      )}

      {user && !showEmailInputOnly ? (
        <div>
          <p className="flex gap-1 mb-2 text-caption-sm-strong-d">
            {hasWallets ? 'Permanent EVM Wallet' : 'EVM Wallet'}
          </p>
          {hasWallets ? (
            <WalletsList wallets={evmWallets} withConnectWallet={false} withActionButtons={false} />
          ) : (
            <DialogButtonsWrapper className="xs-msq:mt-2 flex-col sm-msq:!flex-row">
              <button
                disabled={isDisabled}
                onClick={() => connectWallet(WalletConnectType.EVM)}
                className="msq-btn msq-btn-lg msq-btn-secondary w-full md-msq:w-[116px]"
              >
                {isLoading && walletConnectType === WalletConnectType.EVM ? (
                  <Spinner size="sm" color="disabled" />
                ) : (
                  <>
                    <WalletConnect className="icon-xs mr-2" />
                    <span>Wallet Connect</span>
                  </>
                )}
              </button>
              <button
                disabled={isDisabled}
                onClick={() => connectWallet(WalletConnectType.PASSKEY)}
                className="msq-btn msq-btn-lg msq-btn-secondary w-full md-msq:w-[116px]"
              >
                {isLoading && walletConnectType === WalletConnectType.PASSKEY ? (
                  <Spinner size="sm" color="disabled" />
                ) : (
                  <>
                    <PasskeysRound className="icon-xs mr-2" />
                    <span>Create Wallet</span>
                  </>
                )}
              </button>
            </DialogButtonsWrapper>
          )}
        </div>
      ) : null}
    </form>
  );
};
